// components/Sender/BulkWorkersDialog.js
import React, { useMemo, useState } from "react";
import {
  WORKER_KIND, WORKER_TYPE, filterWorkers, uniqueValues, pickRandom,
  namesToGroupKeys, workerAllGroupKeys,
} from "./workers";

function _toggle(set, v) {
  const next = new Set(set);
  if (next.has(v)) next.delete(v);
  else next.add(v);
  return next;
}

export function BulkWorkersDialog({
  availableWorkers,          // воркеры, отфильтрованные по каналу кампании
  alreadyInBlock,            // Set имён, уже добавленных в ТЕКУЩИЙ блок
  usedInOtherBlocks,         // Set имён из ДРУГИХ блоков шаблона
  onAdd, onClose,
}) {
  const [kinds, setKinds]           = useState(new Set());
  const [types, setTypes]           = useState(new Set());
  const [providers, setProviders]   = useState(new Set());
  const [pripiskas, setPripiskas]   = useState(new Set());
  const [search, setSearch]         = useState("");
  const [onlyActive, setOnlyActive] = useState(true);
  const [excludeUsed, setExcludeUsed] = useState(true);
  const [mode, setMode]             = useState("all");
  const [count, setCount]           = useState(10);
  const [pctMode, setPctMode]       = useState("even");
  const [fixedPct, setFixedPct]     = useState(100);

  const allKinds     = useMemo(() => uniqueValues(availableWorkers, "_kind"), [availableWorkers]);
  const allTypes     = useMemo(() => uniqueValues(availableWorkers, "_type"), [availableWorkers]);
  const allProviders = useMemo(() => uniqueValues(availableWorkers, "_provider"), [availableWorkers]);
  const allPripiskas = useMemo(() => uniqueValues(availableWorkers, "_pripiska"), [availableWorkers]);

  // ─── Единый набор group keys, которые уже заняты ───
  const combinedExclusionKeys = useMemo(() => {
    const s = new Set();
    namesToGroupKeys(alreadyInBlock).forEach((k) => s.add(k));
    if (usedInOtherBlocks) {
      namesToGroupKeys(usedInOtherBlocks).forEach((k) => s.add(k));
    }
    return s;
  }, [alreadyInBlock, usedInOtherBlocks]);

  // Базовые фильтры (без exclusion) — нужно для подсчёта "исключено"
  const baseFilters = {
    kinds, types, providers, pripiskas, search, onlyActive,
  };

  const baseFiltered = useMemo(
    () => filterWorkers(availableWorkers, baseFilters),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [availableWorkers, kinds, types, providers, pripiskas, search, onlyActive]
  );

  const filtered = useMemo(
    () => filterWorkers(availableWorkers, {
      ...baseFilters,
      excludeGroupKeys: excludeUsed ? combinedExclusionKeys : null,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [availableWorkers, kinds, types, providers, pripiskas, search,
     onlyActive, excludeUsed, combinedExclusionKeys]
  );

  const excludedByUsage = baseFiltered.length - filtered.length;

  const toAdd = useMemo(() => {
    if (mode === "all") return filtered;
    if (mode === "first") return filtered.slice(0, Math.max(0, count));
    if (mode === "random") return pickRandom(filtered, count);
    return filtered;
  }, [filtered, mode, count]);

  const pctEach = pctMode === "even"
    ? (toAdd.length > 0 ? Math.round((100 / toAdd.length) * 100) / 100 : 0)
    : Math.max(0, Number(fixedPct) || 0);

  const totalPct = pctEach * toAdd.length;

  const confirm = () => {
    if (!toAdd.length) return;
    onAdd(toAdd, pctEach);
    onClose();
  };

  const resetFilters = () => {
    setKinds(new Set()); setTypes(new Set());
    setProviders(new Set()); setPripiskas(new Set());
    setSearch(""); setOnlyActive(true); setExcludeUsed(true);
  };

  return (
    <div style={overlay} onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} style={card}>
        <div style={header}>
          <div>
            <h3 style={{ margin: 0 }}>➕ Массово добавить воркеров</h3>
            <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>
              Всего: {availableWorkers.length} · после фильтров:{" "}
              <b>{filtered.length}</b>
              {excludeUsed && excludedByUsage > 0 && (
                <span style={{ color: "#b45309" }}>
                  {" "}· исключено (уже в шаблоне): <b>{excludedByUsage}</b>
                </span>
              )}
              {excludeUsed && combinedExclusionKeys.size > 0 && (
                <span style={{ color: "#9ca3af" }}>
                  {" "}(занятых групп: {combinedExclusionKeys.size})
                </span>
              )}
            </div>
          </div>
          <button className="btn-cancel" onClick={onClose}>✕</button>
        </div>

        <div style={body}>
          <div style={filterGrid}>
            <FilterGroup
              label="🚚 Транспорт"
              options={allKinds}
              renderLabel={(k) => `${WORKER_KIND[k]?.icon || ""} ${WORKER_KIND[k]?.label || k}`}
              selected={kinds}
              onToggle={(v) => setKinds(_toggle(kinds, v))}
            />
            <FilterGroup
              label="📨 Тип воркера"
              options={allTypes}
              renderLabel={(k) => `${WORKER_TYPE[k]?.icon || ""} ${WORKER_TYPE[k]?.label || k}`}
              selected={types}
              onToggle={(v) => setTypes(_toggle(types, v))}
            />
            <FilterGroup
              label="🏢 Провайдер"
              options={allProviders}
              renderLabel={(p) => p || "—"}
              selected={providers}
              onToggle={(v) => setProviders(_toggle(providers, v))}
            />
            <FilterGroup
              label="🏷 Приписка"
              options={allPripiskas}
              renderLabel={(p) => p || "—"}
              selected={pripiskas}
              onToggle={(v) => setPripiskas(_toggle(pripiskas, v))}
            />
          </div>

          <div style={{ display: "flex", gap: 12, alignItems: "center",
                        flexWrap: "wrap", marginTop: 10 }}>
            <input className="form-input" placeholder="🔍 Поиск по имени…"
                   style={{ flex: 1, minWidth: 240 }}
                   value={search}
                   onChange={(e) => setSearch(e.target.value)} />
            <label style={checkLabel}>
              <input type="checkbox" checked={onlyActive}
                     onChange={(e) => setOnlyActive(e.target.checked)} />
              только активные
            </label>
            <label style={checkLabel}
                   title="Группировка по ID: WORKER_*_3_* считается одним аккаунтом, независимо от блока">
              <input type="checkbox" checked={excludeUsed}
                     onChange={(e) => setExcludeUsed(e.target.checked)} />
              исключить уже в шаблоне
            </label>
            <button className="btn-cancel" onClick={resetFilters}
                    style={{ fontSize: 12, padding: "4px 10px" }}>
              Сбросить
            </button>
          </div>

          <div style={{
            marginTop: 16, padding: 14, background: "#f9fafb",
            borderRadius: 10, border: "1px solid #e5e7eb",
          }}>
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap",
                          alignItems: "center" }}>
              <div style={{ fontWeight: 700 }}>Режим:</div>
              <label style={radioLabel}>
                <input type="radio" checked={mode === "all"}
                       onChange={() => setMode("all")} />
                Все подходящие ({filtered.length})
              </label>
              <label style={radioLabel}>
                <input type="radio" checked={mode === "first"}
                       onChange={() => setMode("first")} />
                Первые
              </label>
              <label style={radioLabel}>
                <input type="radio" checked={mode === "random"}
                       onChange={() => setMode("random")} />
                Случайные
              </label>
              {(mode === "first" || mode === "random") && (
                <input className="form-input" type="number" min={1}
                       max={filtered.length}
                       style={{ width: 100 }}
                       value={count}
                       onChange={(e) =>
                         setCount(Math.max(1, parseInt(e.target.value, 10) || 1))} />
              )}
            </div>

            <div style={{ display: "flex", gap: 12, flexWrap: "wrap",
                          alignItems: "center", marginTop: 10 }}>
              <div style={{ fontWeight: 700 }}>Проценты:</div>
              <label style={radioLabel}>
                <input type="radio" checked={pctMode === "even"}
                       onChange={() => setPctMode("even")} />
                Равномерно (по {toAdd.length ? Math.round(100 / toAdd.length) : 0}%)
              </label>
              <label style={radioLabel}>
                <input type="radio" checked={pctMode === "fixed"}
                       onChange={() => setPctMode("fixed")} />
                Одинаково
              </label>
              {pctMode === "fixed" && (
                <input className="form-input" type="number" min={1} max={1000}
                       style={{ width: 100 }}
                       value={fixedPct}
                       onChange={(e) =>
                         setFixedPct(parseInt(e.target.value, 10) || 0)} />
              )}
              {pctMode === "fixed" && <span style={{ fontSize: 12,
                color: "#6b7280" }}>% каждому</span>}
            </div>

            <div style={{
              marginTop: 10, fontSize: 13, lineHeight: 1.6,
              color: totalPct > 100 ? "#dc2626" : "#374151",
            }}>
              <b>Итого будет добавлено:</b> {toAdd.length} воркер(ов) × {pctEach}% ={" "}
              <b style={{ color: totalPct > 100 ? "#dc2626" : "#16a34a" }}>
                {totalPct.toFixed(1)}%
              </b>
              {totalPct > 100 && "  ⚠️ больше 100% — суммарный вес будет перекошен"}
              {totalPct < 100 && totalPct > 0 && (
                <span style={{ color: "#b45309" }}>
                  {"  "}(норма — 100%, у остальных воркеров остаётся{" "}
                  {(100 - totalPct).toFixed(1)}%)
                </span>
              )}
            </div>
          </div>

          <div style={{ marginTop: 12 }}>
            <div style={{ fontWeight: 700, marginBottom: 8, fontSize: 13 }}>
              Превью ({Math.min(toAdd.length, 20)} из {toAdd.length})
            </div>
            <div style={{
              maxHeight: 240, overflow: "auto",
              background: "#fff", border: "1px solid #e5e7eb",
              borderRadius: 8, padding: 8,
            }}>
              {toAdd.length === 0 ? (
                <div style={{ color: "#9ca3af", padding: 10, fontSize: 13 }}>
                  {excludeUsed && baseFiltered.length > 0
                    ? "Все подходящие уже используются в шаблоне. Снимите «исключить уже в шаблоне», чтобы увидеть полный список."
                    : "Ничего не подходит под фильтры."}
                </div>
              ) : (
                toAdd.slice(0, 20).map((w) => (
                  <div key={w.id || w._name} style={{
                    display: "flex", alignItems: "center", gap: 8,
                    padding: "4px 6px", fontSize: 12,
                    fontFamily: "Consolas, monospace",
                  }}>
                    <span style={{ fontSize: 11 }}>
                      {WORKER_KIND[w._kind]?.icon || "?"}
                    </span>
                    <span style={{ flex: 1 }}>{w._name}</span>
                    <span style={{ color: "#9ca3af", fontSize: 11 }}>
                      {w._pripiska || ""} · lim {w._daily_limit || "∞"}
                    </span>
                  </div>
                ))
              )}
              {toAdd.length > 20 && (
                <div style={{ color: "#6b7280", padding: 6, fontSize: 12 }}>
                  … и ещё {toAdd.length - 20}
                </div>
              )}
            </div>
          </div>
        </div>

        <div style={footer}>
          <button className="btn-cancel" onClick={onClose}>Отмена</button>
          <button className="btn-primary" disabled={!toAdd.length}
                  onClick={confirm}>
            ➕ Добавить {toAdd.length} × {pctEach}%
          </button>
        </div>
      </div>
    </div>
  );
}

function FilterGroup({ label, options, renderLabel, selected, onToggle }) {
  if (!options.length) return null;
  return (
    <div>
      <div style={{ fontWeight: 600, fontSize: 12, marginBottom: 6,
                    color: "#374151" }}>{label}</div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
        {options.map((opt) => {
          const checked = selected.has(opt);
          return (
            <button key={opt}
                    onClick={() => onToggle(opt)}
                    style={{
                      padding: "4px 10px", borderRadius: 6, fontSize: 11,
                      border: checked
                        ? "1px solid #3b82f6" : "1px solid #e5e7eb",
                      background: checked ? "#eff6ff" : "#fff",
                      color: checked ? "#1d4ed8" : "#374151",
                      cursor: "pointer", fontWeight: checked ? 700 : 400,
                    }}>
              {renderLabel(opt)}
            </button>
          );
        })}
      </div>
    </div>
  );
}

const overlay = {
  position: "fixed", inset: 0, background: "rgba(0,0,0,.6)",
  backdropFilter: "blur(3px)", zIndex: 1400,
  display: "flex", alignItems: "center", justifyContent: "center",
  padding: 20,
};
const card = {
  background: "#fff", borderRadius: 14, width: "100%",
  maxWidth: 900, maxHeight: "92vh", display: "flex",
  flexDirection: "column", overflow: "hidden",
  boxShadow: "0 20px 60px rgba(0,0,0,.35)",
};
const header = {
  padding: "16px 20px", borderBottom: "1.5px solid #e5e7eb",
  display: "flex", justifyContent: "space-between", alignItems: "center",
};
const body = { flex: 1, overflow: "auto", padding: 20 };
const footer = {
  padding: "12px 20px", borderTop: "1.5px solid #e5e7eb",
  display: "flex", justifyContent: "flex-end", gap: 8,
};
const filterGrid = {
  display: "grid", gridTemplateColumns: "1fr 1fr",
  gap: 14, marginBottom: 10,
};
const checkLabel = {
  display: "flex", gap: 6, alignItems: "center",
  fontSize: 12, color: "#374151", cursor: "pointer",
};
const radioLabel = {
  display: "flex", gap: 6, alignItems: "center",
  fontSize: 13, color: "#374151", cursor: "pointer",
};